import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import ListingCard from '../components/ListingCard';
import SmartSearch from '../components/SmartSearch';
import ServicesTabs from '../components/ServicesTabs';
import EventsTabs from '../components/EventsTabs';
import { Search, Filter, X, ChevronDown, Zap, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListingsPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasLocation = !!(user && user.lat != null && user.lng != null);
  const sortVal = searchParams.get('sort') || 'newest';

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [region, setRegion] = useState(searchParams.get('region') || '');
  const [regions, setRegions] = useState([]);
  const [regionNames, setRegionNames] = useState({});
  const [taxonomy, setTaxonomy] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Taxonomy-based filters
  const [eventSubcategory, setEventSubcategory] = useState(searchParams.get('event_subcategory') || '');
  const [eventType, setEventType] = useState(searchParams.get('event_type') || '');
  const [serviceSubcategory, setServiceSubcategory] = useState(searchParams.get('service_subcategory') || '');
  const [serviceType, setServiceType] = useState(searchParams.get('service_type') || '');
  const [subcategory, setSubcategory] = useState(searchParams.get('subcategory') || '');
  const [districtOptions, setDistrictOptions] = useState([]);
  const districtFilter = searchParams.get('location_district_slug') || '';

  useEffect(() => {
    axios.get(`${API}/regions`).then(({ data }) => { setRegions(data.regions); setRegionNames(data.names); }).catch(() => {});
    axios.get(`${API}/categories`).then(({ data }) => setTaxonomy(data.taxonomy)).catch(() => {});
  }, []);

  // Load nearby microdistricts whenever a specific region (city) is chosen so
  // buyers can narrow listings down to a neighbourhood.
  useEffect(() => {
    const cur = region || searchParams.get('region') || '';
    if (!cur || cur === 'all') { setDistrictOptions([]); return; }
    let active = true;
    (async () => {
      try {
        const { data: rr } = await axios.get(`${API}/locations/resolve-region`, { params: { slug: cur } });
        if (rr.city_lat == null || rr.city_lng == null) { if (active) setDistrictOptions([]); return; }
        const { data } = await axios.get(`${API}/locations/districts-near`, { params: { lat: rr.city_lat, lng: rr.city_lng, radius_km: 12 } });
        if (active) setDistrictOptions(data.districts || []);
      } catch (e) { if (active) setDistrictOptions([]); }
    })();
    return () => { active = false; };
  }, [region, searchParams]);

  useEffect(() => {
    const doFetch = async () => {
      try {
        setLoading(true);
        const params = {};
        for (const [k, v] of searchParams.entries()) { if (v) params[k] = v; }
        // "Closest first" needs the viewer's saved coordinates. If they are not
        // available we silently fall back to the default (newest) ordering.
        if (params.sort === 'nearest') {
          if (hasLocation) { params.lat = user.lat; params.lng = user.lng; }
          else { delete params.sort; }
        }
        const { data } = await axios.get(`${API}/listings`, { params });
        setListings(data);
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    doFetch();
  }, [searchParams, hasLocation, user]);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value && value !== 'all') { p.set(key, value); } else { p.delete(key); }
    setSearchParams(p);
  };

  // Pill className for always-visible subcategory/type filters.
  const subPill = (active, small = false) =>
    `${small ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} rounded-full font-medium border transition-all ${
      active
        ? 'bg-[#D97736] text-white border-[#D97736] shadow-sm'
        : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736] hover:text-[#D97736]'
    }`;

  const handleCategoryChange = (value) => {
    setCategory(value);
    // Reset sub-filters when category changes
    setEventSubcategory(''); setEventType(''); setServiceSubcategory(''); setServiceType(''); setSubcategory('');
    const p = new URLSearchParams();
    if (value && value !== 'all') p.set('category', value);
    if (searchQuery) p.set('search', searchQuery);
    if (region && region !== 'all') p.set('region', region);
    setSearchParams(p);
  };

  const clearFilters = () => {
    setSearchQuery(''); setCategory(''); setRegion('');
    setEventSubcategory(''); setEventType(''); setServiceSubcategory(''); setServiceType(''); setSubcategory('');
    setSearchParams({});
  };

  const hasFilters = Array.from(searchParams.entries()).length > 0;
  const categories = ['shop', 'service', 'event', 'project', 'business'];
  const activeCat = searchParams.get('category') || category;

  // Get taxonomy subcategories for active category
  const taxCat = taxonomy?.[activeCat];
  const hasSubFilters = activeCat === 'event' || activeCat === 'service' || activeCat === 'shop';

  const getPageTitle = () => {
    const cat = searchParams.get('category');
    if (cat) return t(cat);
    return language === 'lv' ? 'Visi sludinājumi' : language === 'ru' ? 'Все объявления' : 'All Listings';
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="listings-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-bold text-[#121212] mb-2">{getPageTitle()}</h1>
          <p className="text-[#5C5C5C]">
            {loading ? t('loading') : `${listings.length} ${language === 'lv' ? 'sludinājumi' : language === 'ru' ? 'объявлений' : 'listings'}`}
          </p>
        </div>

        {/* Services / Gigs tabs — only on the Services experience */}
        {activeCat === 'service' && (
          <div className="mb-6 space-y-4">
            <ServicesTabs active="providers" />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[#FFF6F0] border border-[#F0D9C8] rounded-2xl p-4" data-testid="need-specialist-cta">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D97736]/10 flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5 text-[#D97736]" />
                </div>
                <div>
                  <p className="font-medium text-[#121212]">
                    {language === 'lv' ? 'Vajag meistaru?' : language === 'ru' ? 'Нужен мастер?' : 'Need a specialist?'}
                  </p>
                  <p className="text-sm text-[#5C5C5C]">
                    {language === 'lv'
                      ? 'Ievieto pieprasījumu, un meistari tuvumā atsauksies paši.'
                      : language === 'ru'
                        ? 'Разместите запрос — мастера рядом откликнутся сами.'
                        : 'Post a request and nearby specialists will reach out to you.'}
                  </p>
                </div>
              </div>
              <Link to="/gigs/create" className="shrink-0">
                <Button className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="post-request-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  {language === 'lv' ? 'Ievietot pieprasījumu' : language === 'ru' ? 'Разместить запрос' : 'Post a request'}
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Event listings / Roles tabs — only on the Events experience */}
        {activeCat === 'event' && (
          <div className="mb-6">
            <EventsTabs active="listings" />
          </div>
        )}

        {/* Main Filters */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4 mb-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1">
              <SmartSearch
                taxonomy={taxonomy}
                initialQuery={searchQuery}
                regionNames={regionNames}
                onApply={(params) => {
                  // Merge new params with the existing region filter when the user
                  // already picked one in the dropdown AND the auto-complete didn't
                  // override it. Otherwise, use the params emitted by SmartSearch.
                  setSearchQuery(params.get('search') || '');
                  setCategory(params.get('category') || '');
                  setRegion(params.get('region') || '');
                  setEventSubcategory(params.get('event_subcategory') || '');
                  setEventType(params.get('event_type') || '');
                  setServiceSubcategory(params.get('service_subcategory') || '');
                  setServiceType(params.get('service_type') || '');
                  setSubcategory(params.get('subcategory') || '');
                  setSearchParams(params);
                }}
              />
            </div>

            <Select value={category || activeCat} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full lg:w-48 h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="category-filter">
                <SelectValue placeholder={t('category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'lv' ? 'Visas kategorijas' : language === 'ru' ? 'Все категории' : 'All Categories'}</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{t(c)}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={region} onValueChange={(v) => { setRegion(v); const p = new URLSearchParams(searchParams); if (v && v !== 'all') p.set('region', v); else p.delete('region'); p.delete('location_district_slug'); setSearchParams(p); }}>
              <SelectTrigger className="w-full lg:w-48 h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="region-filter">
                <SelectValue placeholder={t('region')} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">{language === 'lv' ? 'Visi reģioni' : language === 'ru' ? 'Все регионы' : 'All Regions'}</SelectItem>
                {regions.map(r => <SelectItem key={r} value={r}>{regionNames[r] || r}</SelectItem>)}
              </SelectContent>
            </Select>

            {districtOptions.length > 0 && (
              <Select value={districtFilter || 'all'} onValueChange={(v) => updateParam('location_district_slug', v)}>
                <SelectTrigger className="w-full lg:w-48 h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="microdistrict-filter">
                  <SelectValue placeholder={language === 'lv' ? 'Mikrorajons' : language === 'ru' ? 'Микрорайон' : 'Microdistrict'} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">{language === 'lv' ? 'Visi mikrorajoni' : language === 'ru' ? 'Все микрорайоны' : 'All microdistricts'}</SelectItem>
                  {districtOptions.map(d => <SelectItem key={d.slug} value={d.slug} data-testid={`microdistrict-filter-option-${d.slug}`}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {hasLocation && (
              <Select value={sortVal} onValueChange={(v) => updateParam('sort', v === 'newest' ? '' : v)}>
                <SelectTrigger className="w-full lg:w-48 h-11 bg-[#FAFAFA] border-[#E5E4E0]" data-testid="sort-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest" data-testid="sort-option-newest">{language === 'lv' ? 'Jaunākie' : language === 'ru' ? 'Новые' : 'Newest'}</SelectItem>
                  <SelectItem value="nearest" data-testid="sort-option-nearest">{language === 'lv' ? 'Tuvākie vispirms' : language === 'ru' ? 'Сначала ближайшие' : 'Closest first'}</SelectItem>
                </SelectContent>
              </Select>
            )}

            {hasFilters && (
              <Button variant="outline" onClick={clearFilters} className="h-11 border-[#E5E4E0] text-[#5C5C5C]" data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-1" /> {language === 'lv' ? 'Notīrīt' : language === 'ru' ? 'Очистить' : 'Clear'}
              </Button>
            )}
          </div>

        </div>

        {/* Advanced Taxonomy Filters — always visible (no dropdown toggle) */}
        {hasSubFilters && taxCat && (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-4 mb-4" data-testid="advanced-filters">
            <div className="flex items-center gap-1.5 mb-3 text-sm font-medium text-[#121212]">
              <Filter className="w-4 h-4 text-[#D97736]" />
              {language === 'lv' ? 'Detalizēti filtri' : language === 'ru' ? 'Расширенные фильтры' : 'Advanced Filters'}
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Event subcategory filter */}
              {activeCat === 'event' && (
                <div className="flex flex-col gap-3 w-full">
                  <div className="flex flex-wrap gap-2" data-testid="event-subcategory-pills">
                    <button
                      type="button"
                      onClick={() => { setEventSubcategory(''); setEventType(''); const p = new URLSearchParams(searchParams); p.delete('event_type'); p.delete('event_subcategory'); setSearchParams(p); }}
                      className={subPill(!eventSubcategory || eventSubcategory === 'all')}
                      data-testid="event-sub-pill-all"
                    >
                      {language === 'lv' ? 'Visi veidi' : language === 'ru' ? 'Все типы' : 'All types'}
                    </button>
                    {Object.entries(taxCat.subcategories).map(([k, s]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => { setEventSubcategory(k); setEventType(''); const p = new URLSearchParams(searchParams); p.delete('event_type'); p.set('event_subcategory', k); setSearchParams(p); }}
                        className={subPill(eventSubcategory === k)}
                        data-testid={`event-sub-pill-${k}`}
                      >
                        {language === 'lv' ? s.label_lv : s.label_en}
                      </button>
                    ))}
                  </div>
                  {eventSubcategory && eventSubcategory !== 'all' && taxCat.subcategories[eventSubcategory]?.types && (
                    <div className="flex flex-wrap gap-2 pl-1 border-l-2 border-[#E5E4E0]" data-testid="event-type-pills">
                      <button type="button" onClick={() => { setEventType(''); updateParam('event_type', 'all'); }} className={subPill(!eventType || eventType === 'all', true)} data-testid="event-type-pill-all">
                        {language === 'lv' ? 'Visi' : language === 'ru' ? 'Все' : 'All'}
                      </button>
                      {Object.entries(taxCat.subcategories[eventSubcategory].types).map(([k, t]) => (
                        <button key={k} type="button" onClick={() => { setEventType(k); updateParam('event_type', k); }} className={subPill(eventType === k, true)} data-testid={`event-type-pill-${k}`}>
                          {language === 'lv' ? t.lv : t.en}{t.age && t.age !== '0+' && t.age !== null ? ` (${t.age})` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Service subcategory filter */}
              {activeCat === 'service' && (
                <div className="flex flex-col gap-3 w-full">
                  <div className="flex flex-wrap gap-2" data-testid="service-subcategory-pills">
                    <button
                      type="button"
                      onClick={() => { setServiceSubcategory(''); setServiceType(''); const p = new URLSearchParams(searchParams); p.delete('service_type'); p.delete('service_subcategory'); setSearchParams(p); }}
                      className={subPill(!serviceSubcategory || serviceSubcategory === 'all')}
                      data-testid="service-sub-pill-all"
                    >
                      {language === 'lv' ? 'Visi veidi' : language === 'ru' ? 'Все типы' : 'All types'}
                    </button>
                    {Object.entries(taxCat.subcategories).map(([k, s]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => { setServiceSubcategory(k); setServiceType(''); const p = new URLSearchParams(searchParams); p.delete('service_type'); p.set('service_subcategory', k); setSearchParams(p); }}
                        className={subPill(serviceSubcategory === k)}
                        data-testid={`service-sub-pill-${k}`}
                      >
                        {language === 'lv' ? s.label_lv : s.label_en}
                      </button>
                    ))}
                  </div>
                  {serviceSubcategory && serviceSubcategory !== 'all' && taxCat.subcategories[serviceSubcategory]?.types && (
                    <div className="flex flex-wrap gap-2 pl-1 border-l-2 border-[#E5E4E0]" data-testid="service-type-pills">
                      <button type="button" onClick={() => { setServiceType(''); updateParam('service_type', 'all'); }} className={subPill(!serviceType || serviceType === 'all', true)} data-testid="service-type-pill-all">
                        {language === 'lv' ? 'Visi' : language === 'ru' ? 'Все' : 'All'}
                      </button>
                      {Object.entries(taxCat.subcategories[serviceSubcategory].types).map(([k, t]) => (
                        <button key={k} type="button" onClick={() => { setServiceType(k); updateParam('service_type', k); }} className={subPill(serviceType === k, true)} data-testid={`service-type-pill-${k}`}>
                          {language === 'lv' ? t.lv : t.en}{t.age && t.age !== '0+' && t.age !== null ? ` (${t.age})` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Shop subcategory filter — pill buttons (canonical 4 only) */}
              {activeCat === 'shop' && (
                <div className="flex flex-wrap gap-2" data-testid="shop-subcategory-pills">
                  <button
                    type="button"
                    onClick={() => { setSubcategory(''); updateParam('subcategory', 'all'); }}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                      !subcategory || subcategory === 'all'
                        ? 'bg-[#D97736] text-white border-[#D97736] shadow-sm'
                        : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736] hover:text-[#D97736]'
                    }`}
                    data-testid="shop-sub-pill-all"
                  >
                    {language === 'lv' ? 'Visas' : language === 'ru' ? 'Все' : 'Show All'}
                  </button>
                  {Object.entries(taxCat.subcategories).map(([k, s]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => { setSubcategory(k); updateParam('subcategory', k); }}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                        subcategory === k
                          ? 'bg-[#D97736] text-white border-[#D97736] shadow-sm'
                          : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#D97736] hover:text-[#D97736]'
                      }`}
                      data-testid={`shop-sub-pill-${k}`}
                    >
                      {language === 'lv' ? s.label_lv : s.label_en}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Active filter badges */}
            <div className="flex flex-wrap gap-2 mt-3">
              {eventSubcategory && eventSubcategory !== 'all' && (
                <Badge variant="outline" className="text-xs bg-[#D97736]/10 text-[#D97736] border-[#D97736]/30">
                  {taxCat?.subcategories?.[eventSubcategory]?.[language === 'lv' ? 'label_lv' : 'label_en'] || eventSubcategory}
                  <button onClick={() => { setEventSubcategory(''); setEventType(''); const p = new URLSearchParams(searchParams); p.delete('event_subcategory'); p.delete('event_type'); setSearchParams(p); }} className="ml-1"><X className="w-3 h-3" /></button>
                </Badge>
              )}
              {serviceSubcategory && serviceSubcategory !== 'all' && (
                <Badge variant="outline" className="text-xs bg-[#4A6B53]/10 text-[#4A6B53] border-[#4A6B53]/30">
                  {taxCat?.subcategories?.[serviceSubcategory]?.[language === 'lv' ? 'label_lv' : 'label_en'] || serviceSubcategory}
                  <button onClick={() => { setServiceSubcategory(''); setServiceType(''); const p = new URLSearchParams(searchParams); p.delete('service_subcategory'); p.delete('service_type'); setSearchParams(p); }} className="ml-1"><X className="w-3 h-3" /></button>
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={`skel-${i}`} className="bg-white rounded-xl h-72 animate-pulse" />
            ))}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Search className="w-16 h-16 mx-auto mb-4 text-[#5C5C5C] opacity-20" />
            <h3 className="font-outfit text-xl font-semibold text-[#121212] mb-2">
              {language === 'lv' ? 'Nav atrasti sludinājumi' : language === 'ru' ? 'Объявления не найдены' : 'No listings found'}
            </h3>
            <p className="text-[#5C5C5C] mb-6">
              {language === 'lv' ? 'Mēģiniet mainīt meklēšanas kritērijus' : language === 'ru' ? 'Попробуйте изменить критерии поиска' : 'Try adjusting your search criteria'}
            </p>
            {hasFilters && (
              <Button onClick={clearFilters} variant="outline" className="border-[#D97736] text-[#D97736]">
                <X className="w-4 h-4 mr-1" /> {language === 'lv' ? 'Notīrīt filtrus' : language === 'ru' ? 'Очистить фильтры' : 'Clear Filters'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
